import { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { APP_NAME, APP_VERSION, BUILT_BY, CHANGELOG, COMPANY_DISPLAY_NAME, COPYRIGHT_YEAR } from '../lib/appInfo';

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

export function WhatsNewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper" aria-labelledby="whats-new-title">
      <DialogTitle sx={{ pr: 7, pb: 2 }}>
        <Typography id="whats-new-title" variant="h6" component="h2">
          What's new in {APP_NAME}
        </Typography>
        <Typography variant="caption">You're on version {APP_VERSION}</Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 16, top: 18 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {CHANGELOG.map((release) => (
            <Box component="section" key={release.version}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" component="h3">
                  Version {release.version}
                </Typography>
                {release.version === APP_VERSION && <Chip size="small" color="primary" label="Current" sx={{ height: 20 }} />}
                <Typography variant="caption" sx={{ ml: 'auto !important' }}>
                  {formatDate(release.date)}
                </Typography>
              </Stack>
              {release.sections.map((section) => (
                <Box key={section.title} sx={{ mb: 1 }}>
                  <Typography variant="caption" component="h4" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {section.title}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {section.items.map((item) => (
                      <Typography component="li" variant="body2" key={item} sx={{ mb: 0.25 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

/** Company name, AST IT Lab credit, version and a link to the changelog. */
export function AppFooter() {
  const [open, setOpen] = useState(false);
  return (
    <Box
      component="footer"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mt: 6,
        pt: 2.5,
        pb: 1,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption">
        © {COPYRIGHT_YEAR} {COMPANY_DISPLAY_NAME}
      </Typography>
      <Typography variant="caption">
        {APP_NAME} by {BUILT_BY} ·{' '}
        <Link component="button" type="button" variant="caption" onClick={() => setOpen(true)} sx={{ verticalAlign: 'baseline' }}>
          v{APP_VERSION} · What's new
        </Link>
      </Typography>
      <WhatsNewDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}
